export type PalletFamily = 'GB' | 'EU';

export type PalletTier = 'Quarter' | 'Half' | 'Full' | 'Custom' | 'Oversize';

export interface PalletFootprint {
  lengthCm: number;
  widthCm: number;
}

export interface PalletTierCaps {
  maxHeightCm: number;
  maxWeightKg: number;
}

export interface PalletType {
  value: string;
  shortLabel: string;
  family: PalletFamily | null;
  tier: PalletTier;
  footprint: PalletFootprint | null;
  tareKg: number | null;
  tierCaps: PalletTierCaps | null;
}

const GB_BOARD: PalletFootprint = { lengthCm: 120, widthCm: 100 };
const GB_HALF_BOARD: PalletFootprint = { lengthCm: 100, widthCm: 60 };
const EU_BOARD: PalletFootprint = { lengthCm: 120, widthCm: 80 };
const EU_HALF_BOARD: PalletFootprint = { lengthCm: 60, widthCm: 80 };

const FULL_BOARD_TARE_KG = 25;
const HALF_BOARD_TARE_KG = 12.5;

const QUARTER_TIER_CAPS: PalletTierCaps = { maxHeightCm: 60, maxWeightKg: 250 };
const HALF_TIER_CAPS: PalletTierCaps = { maxHeightCm: 100, maxWeightKg: 500 };
const FULL_TIER_CAPS: PalletTierCaps = { maxHeightCm: 220, maxWeightKg: 1250 };

export const DEFAULT_MAXIMUM_PALLET_WEIGHT_KG = 1250;

export const PALLET_TYPES: PalletType[] = [
  {
    value: 'GB Quarter Pallet',
    shortLabel: 'GB Quarter',
    family: 'GB',
    tier: 'Quarter',
    footprint: GB_BOARD,
    tareKg: FULL_BOARD_TARE_KG,
    tierCaps: QUARTER_TIER_CAPS,
  },
  {
    value: 'GB Half Pallet',
    shortLabel: 'GB Half',
    family: 'GB',
    tier: 'Half',
    footprint: GB_HALF_BOARD,
    tareKg: HALF_BOARD_TARE_KG,
    tierCaps: HALF_TIER_CAPS,
  },
  {
    value: 'GB Pallet',
    shortLabel: 'GB Full',
    family: 'GB',
    tier: 'Full',
    footprint: GB_BOARD,
    tareKg: FULL_BOARD_TARE_KG,
    tierCaps: FULL_TIER_CAPS,
  },
  {
    value: 'EU Quarter Pallet',
    shortLabel: 'EU Quarter',
    family: 'EU',
    tier: 'Quarter',
    footprint: EU_BOARD,
    tareKg: FULL_BOARD_TARE_KG,
    tierCaps: QUARTER_TIER_CAPS,
  },
  {
    value: 'EU Half Pallet',
    shortLabel: 'EU Half',
    family: 'EU',
    tier: 'Half',
    footprint: EU_HALF_BOARD,
    tareKg: HALF_BOARD_TARE_KG,
    tierCaps: HALF_TIER_CAPS,
  },
  {
    value: 'EU Pallet',
    shortLabel: 'EU Full',
    family: 'EU',
    tier: 'Full',
    footprint: EU_BOARD,
    tareKg: FULL_BOARD_TARE_KG,
    tierCaps: FULL_TIER_CAPS,
  },
  {
    value: 'Oversize Half Pallet',
    shortLabel: 'Oversize Half',
    family: null,
    tier: 'Oversize',
    footprint: null,
    tareKg: null,
    tierCaps: null,
  },
  {
    value: 'Oversize Pallet',
    shortLabel: 'Oversize Full',
    family: null,
    tier: 'Oversize',
    footprint: null,
    tareKg: null,
    tierCaps: null,
  },
  {
    value: 'Custom Pallet',
    shortLabel: 'Custom',
    family: null,
    tier: 'Custom',
    footprint: null,
    tareKg: null,
    tierCaps: null,
  },
];

export const DEFAULT_PALLET_TYPE = 'GB Pallet';

export const STANDARD_PALLET_TYPE_VALUES: string[] = PALLET_TYPES.filter(
  (type) => type.footprint !== null
).map((type) => type.value);

export function findPalletType(
  value: string | null | undefined
): PalletType | undefined {
  return PALLET_TYPES.find((type) => type.value === value);
}

export function isStandardPalletType(
  value: string | null | undefined
): boolean {
  return findPalletType(value)?.footprint != null;
}

export function getPalletFootprintMetres(
  value: string | null | undefined
): { lengthM: number; widthM: number } | null {
  const footprint = findPalletType(value)?.footprint;
  if (!footprint) return null;
  return { lengthM: footprint.lengthCm / 100, widthM: footprint.widthCm / 100 };
}

export function getPalletTypeLabel(value: string | null | undefined): string {
  const type = findPalletType(value);
  if (!type || !type.footprint) return value ?? '';
  return `${type.shortLabel} Pallet`;
}

export function getPalletTareKg(
  value: string | null | undefined
): number | null {
  return findPalletType(value)?.tareKg ?? null;
}

function isMeasured(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function describeExcessKind(isTooTall: boolean, isTooHeavy: boolean): string {
  if (isTooTall && isTooHeavy) return 'more';
  return isTooTall ? 'taller' : 'heavier';
}

function describeExcessOverDefaultMaximumWeight(
  loadWeightKg: number | null | undefined
): string | null {
  if (
    !isMeasured(loadWeightKg) ||
    loadWeightKg <= DEFAULT_MAXIMUM_PALLET_WEIGHT_KG
  )
    return null;
  return `This load is heavier than ${DEFAULT_MAXIMUM_PALLET_WEIGHT_KG} kg per pallet. Pallet networks quote it only if they accept heavier pallets.`;
}

export function describeTierExcess(
  palletType: string | null | undefined,
  loadHeightCm: number | null | undefined,
  loadWeightKg: number | null | undefined
): string | null {
  const type = findPalletType(palletType);
  if (!type) return null;
  const caps = type.tierCaps;
  if (!caps) return describeExcessOverDefaultMaximumWeight(loadWeightKg);

  const isTooTall = isMeasured(loadHeightCm) && loadHeightCm > caps.maxHeightCm;
  const isTooHeavy =
    isMeasured(loadWeightKg) && loadWeightKg > caps.maxWeightKg;
  if (!isTooTall && !isTooHeavy) return null;

  const excess = describeExcessKind(isTooTall, isTooHeavy);
  const exceededCaps = [
    ...(isTooTall ? [`${caps.maxHeightCm / 100} m`] : []),
    ...(isTooHeavy ? [`${caps.maxWeightKg} kg`] : []),
  ].join(', ');
  const consequence =
    type.tier === 'Full'
      ? 'It may not be quotable as a single pallet.'
      : 'Carriers will price it as the next tier up.';

  return `This load is ${excess} than ${getPalletTypeLabel(
    type.value
  )} allows (max ${exceededCaps}). ${consequence}`;
}
